<template>
  <v-app style="width: 100%" class="pa-3 pt-0">
    <div class="px-3">
      <div style="display: flex">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" icon class="float-left mt-1" small v-on="on">
              <v-icon color="primary" style="font-size: 22px" @click="print">
                mdi-cloud-print-outline
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("print") }}</span>
        </v-tooltip>
        <v-card-text class="py-0">
          <v-chip-group
            v-model="printSize"
            active-class="deep-purple--text text--accent-4"
            mandatory
          >
            <v-chip small value="29.7cm 21cm"> A4 </v-chip>
            <v-chip small value="21cm 14.8cm"> A5 </v-chip>
            <v-chip small value="14.8cm 10.5cm"> A6 </v-chip>
            <v-chip small value="10.5cm 7.4cm"> A7 </v-chip>
            <v-chip small value="5.2cm 7.4cm"> A7 </v-chip>
            <v-chip v-if="!width && !height" small @click="customSizeDialog = true">
              {{ $t("custom") }}
            </v-chip>
            <v-chip
              v-if="width && height"
              small
              close
              color="primary"
              outlined
              @click:close="clearCustom"
              @click="customSizeDialog = true"
            >
              {{ width + "cm" }} × {{ height + "cm" }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </div>
    </div>
    <div id="print" class="px-3">
      <v-data-table
        v-if="headers"
        :height="showAttachmentSection ? null : tableHeight"
        fixed-header
        ref="table"
        :headers="headers"
        :items="items"
        :items-per-page="8000000000000"
        hide-default-footer
      >
        <template v-slot:item.given="{ item }">
          <div class="align-start">
            {{ item.given?.name }}
          </div>
        </template>
        <template v-slot:item.fin4="{ item }">
          <div class="align-start">
            {{ item.fin4 ? item.fin4.name : "" }}
          </div>
        </template>
        <template v-slot:item.debtor="{ item }">
          <div class="align-start">
            {{ $numberFormat(parseFloat(item.debtor)) }}
          </div>
        </template>
        <template v-slot:item.creditor="{ item }">
          <div class="align-start">
            {{ $numberFormat(parseFloat(item.creditor)) }}
          </div>
        </template>
        <template v-slot:item.row="{ item }">
          <div align="center">
            {{ parseInt(items.indexOf(item)) + parseFloat(1) }}
          </div>
        </template>
        <template v-slot:body.append>
          <tr :class="{ 'v-data-table__mobile-table-row': isMobile }">
            <td
              :colspan="`${isMobile ? 6 : 2}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              <strong>{{ $t("sum") }}</strong>
            </td>
            <td
              :colspan="`${isMobile ? 6 : 1}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            ></td>
            <td
              :colspan="`${isMobile ? 6 : 1}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            ></td>
            <td
              :colspan="`${isMobile ? 6 : 1}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            ></td>
            <td
              :colspan="`${isMobile ? 6 : 1}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              <div class="align-start">
                {{ isMobile ? $t("debtor") + ":" : "" }}
                {{ $numberFormat(totals.debtor) }}
              </div>
            </td>
            <td
              :colspan="`${isMobile ? 6 : 1}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              <div class="align-start">
                {{ isMobile ? $t("creditor") + ":" : "" }}
                {{ $numberFormat(totals.creditor) }}
              </div>
            </td>
          </tr>
          <tr class="signature-append-row">
            <td
              :colspan="`${isMobile ? 6 : 4}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            ></td>
            <td
              :colspan="`${isMobile ? 6 : 3}`"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              <div align="center">{{ $t("signature") }}</div>
            </td>
          </tr>
          <tr
            v-if="showAttachmentSection"
            :class="{ 'v-data-table__mobile-table-row': isMobile }"
          >
            <template v-if="isMobile">
              <td
                :colspan="6"
                :class="{ 'v-data-table__mobile-row': isMobile }"
                class="pa-0"
              ></td>
              <td
                :colspan="6"
                :class="{ 'v-data-table__mobile-row': isMobile }"
                class="attachment-print-td pa-0"
              >
                <div class="attachment-print-section px-0">
                  <div class="attachment-table-scroll">
                    <table class="attachment-table">
                      <thead>
                        <tr>
                          <th>{{ $t("preview") }}</th>
                          <th>{{ $t("fileName") }}</th>
                          <th>{{ $t("fileType") }}</th>
                          <th>{{ $t("size") }}</th>
                        </tr>
                      </thead>
                      <tbody v-if="attachmentRows.length">
                        <tr v-for="row in attachmentRows" :key="row.key">
                          <td>
                            <div class="preview-cell" @click="openAttachment(row)">
                              <v-img
                                v-if="row.isImage"
                                :src="row.link"
                                max-width="36"
                                max-height="36"
                                contain
                              />
                              <v-icon v-else size="22" color="grey darken-1">
                                mdi-file-outline
                              </v-icon>
                            </div>
                          </td>
                          <td class="file-name-cell" :title="row.name">
                            {{ row.name }}
                          </td>
                          <td>{{ row.type }}</td>
                          <td>{{ row.sizeText }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
            </template>
            <td v-else colspan="7" class="attachment-print-td pa-0">
              <div class="attachment-print-section px-0">
                <div class="attachment-table-scroll">
                  <table class="attachment-table">
                    <thead>
                      <tr>
                        <th>{{ $t("preview") }}</th>
                        <th>{{ $t("fileName") }}</th>
                        <th>{{ $t("fileType") }}</th>
                        <th>{{ $t("size") }}</th>
                      </tr>
                    </thead>
                    <tbody v-if="attachmentRows.length">
                      <tr v-for="row in attachmentRows" :key="row.key">
                        <td>
                          <div class="preview-cell" @click="openAttachment(row)">
                            <v-img
                              v-if="row.isImage"
                              :src="row.link"
                              max-width="36"
                              max-height="36"
                              contain
                            />
                            <v-icon v-else size="22" color="grey darken-1">
                              mdi-file-outline
                            </v-icon>
                          </div>
                        </td>
                        <td class="file-name-cell" :title="row.name">
                          {{ row.name }}
                        </td>
                        <td>{{ row.type }}</td>
                        <td>{{ row.sizeText }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="customSizeDialog"
      width="60%"
    >
      <v-card>
        <v-col class="d-flex pb-0">
          <v-btn icon color="grey darken-2" @click="customSizeDialog = false">
            <v-icon class="close-hover" large> mdi-close </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("print-size") }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <v-col cols="12" sm="6" md="6">
            <v-text-field
              v-model="width"
              outlined
              dense
              type="number"
              :label="$t('width') + '(cm)'"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <v-text-field
              v-model="height"
              outlined
              dense
              type="number"
              :label="$t('height') + '(cm)'"
            />
          </v-col>
        </v-row>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn color="primary" @click="setCustomeSize">
                {{ $t("submit") }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="customSizeDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import store from "../../../../store";
import General from "../../../../Mixins/general";
export default {
  name: "AccountingPrint",
  mixins: [General],
  props: ["headerProp", "itemsProp", "title", "idProp"],
  data() {
    return {
      attachments: null,
      headerPrint: [],
      items: [],
      time: null,
      type: null,
      content: null,
      id: null,
      date: null,
      account: null,
      number: null,
      currency_amount: null,
      dueDate: null,
      currency: null,
      tax: null,
      discount: null,
      entrepot: null,
      totalTax: null,
      totalDiscount: null,
      totalCount: null,
      totalFee: null,
      sum: null,
      width: null,
      height: null,
      user: null,
      customer: null,
      self: null,
      turningNumber: null,
      dailyNumber: null,
      headers: null,
      customSizeDialog: null,
      printSize: "29.7cm 21cm",
    };
  },
  computed: {
    /** attachment*/
    attachmentRows() {
      const list = this.attachments;
      if (!list || !Array.isArray(list) || list.length === 0) {
        return [];
      }
      const imageExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".bmp",
        ".JPG",
        ".PNG",
        ".JPEG",
      ];
      return list.map((file, index) => {
        let ext = (file.extension || "").toLowerCase();
        if (ext && ext.charAt(0) !== ".") {
          ext = "." + ext;
        }
        const isImage =
          (file.type && String(file.type).indexOf("image/") === 0) ||
          imageExtensions.includes(ext);
        const name = file.name || file.title || "-";
        let typeExt = ext;
        if (!typeExt && name && typeof name === "string" && name.includes(".")) {
          const last = name.split(".").pop();
          if (last && last.length <= 10 && last.length < name.length) {
            typeExt = "." + last.toLowerCase();
          }
        }
        if (!typeExt && file.type && String(file.type).indexOf("/") !== -1) {
          const sub = String(file.type).split("/")[1].split(";")[0].split("+")[0].trim();
          if (sub) {
            typeExt = "." + sub.toLowerCase();
          }
        }
        const typeLabel = typeExt || "-";
        const bytes = file.size != null ? Number(file.size) : NaN;
        let sizeText = "-";
        if (!Number.isNaN(bytes) && bytes >= 0) {
          if (bytes >= 1048576) {
            sizeText = (bytes / 1048576).toFixed(2) + " MB";
          } else if (bytes >= 1024) {
            sizeText = (bytes / 1024).toFixed(1) + " KB";
          } else {
            sizeText = bytes + " B";
          }
        }
        return {
          key: file.id != null ? "att-" + file.id : "att-" + index + "-" + name,
          link: file.link || "",
          name,
          type: typeLabel,
          sizeText,
          isImage,
        };
      });
    },
    /** attachment*/
    showAttachmentSection() {
      return (
        this.$options.name === "AccountingPrint" &&
        this.idProp &&
        this.attachmentRows.length > 0
      );
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    totals: function () {
      const totals = this.items.reduce(
        (acc, d) => {
          acc.debtor += parseFloat(d.debtor ? d.debtor : 0);
          acc.creditor += parseFloat(d.creditor ? d.creditor : 0);
          return acc;
        },
        {
          debtor: 0,
          creditor: 0,
        }
      );
      return totals;
    },
  },
  watch: {
    idProp() {
      if (this.idProp) {
        var header = this.$refs.table.$el.querySelector(".v-data-table-header");
        header.deleteRow(0);
        header.deleteRow(0);
        this.show();
      }
    },
    headerProp() {
      // this.head = this.itemsProp
    },
    itemsProp() {
      this.items = this.itemsProp;
    },
  },
  beforeMount() {
    this.headers = [
      {
        text: this.$t("row"),
        align: "start",
        sortable: false,
        value: "row",
        width: 20,
      },
      {
        text: this.$t("content"),
        sortable: false,
        value: "content",
      },
      {
        text: this.$t("given"),
        sortable: true,
        value: "given",
      },
      {
        text: this.$t("detail"),
        sortable: true,
        value: "fin4",
      },
      {
        text: this.$t("count"),
        sortable: true,
        value: "count",
      },
      {
        text: this.$t("debtor"),
        sortable: true,
        value: "debtor",
      },
      {
        text: this.$t("creditor"),
        sortable: true,
        value: "creditor",
      },
    ];
    var storage = JSON.parse(localStorage.getItem("token"));
    if (storage != null) {
      store.commit("SET_START_DATE", storage.init.startDate);
      store.commit("SET_END_DATE", storage.init.endDate);
      store.commit("SET_DATE_LOCALE", storage.init.date_locale.value);
      if (storage.init.date_locale.value === "fa,en") {
        store.commit("SET_DATE_FORMAT", "jYYYY/jMM/jDD");
      } else {
        store.commit("SET_DATE_FORMAT", "YYYY/MM/DD");
        store.commit("SET_START_DATE", null);
        store.commit("SET_END_DATE", null);
      }
      store.commit("SET_RTL", storage.init.rtl.value);
      store.commit("SET_DARK", storage.init.dark.value);
      store.commit("SET_LANG", storage.init.lang.value);
      store.commit("SET_THEME_COLOR", storage.init.theme_color.value);
    }
    this.$vuetify.theme.dark = this.$store.state.dark;
    this.$root.$i18n.locale = this.$store.state.lang;
    this.$vuetify.theme.themes[
      this.$vuetify.theme.isDark ? "dark" : "light"
    ].primary = this.$store.state.theme_color;
    if (this.$store.state.rtl) {
      this.$vuetify.rtl = true;
      storage.init.rtl.value = true;
      localStorage.setItem("token", JSON.stringify(storage));
    } else {
      this.$vuetify.rtl = false;
    }
    if (this.idProp) {
      this.show();
    }
  },
  mounted() {},
  methods: {
    // attachment//
    openAttachment(row) {
      if (!row?.link) return;
      window.open(
        row.link,
        "_blank",
        "toolbar=yes,scrollbars=yes,resizable=yes,width=600,height=500"
      );
    },

    setheader(data) {
      var elm = this.$refs.table.$el.querySelector(".v-data-table-header");
      var tr1 = document.createElement("tr");
      var tr2 = document.createElement("tr");
      tr1.innerHTML =
        "" +
        '<th colspan="7">' +
        '<div style="display: flex">' +
        '<div style="width: 33.33%">' +
        '<div align="right"><img class="mx-1" src="' +
        (this.self.logo ? this.self.logo.link : "") +
        '" width="50"></div>' +
        "</div>" +
        '<div style="width: 33.33%">' +
        '<div align="center" style="font-size: 16px">' +
        this.$t("accountingDocument") +
        " </div>" +
        '<div align="center"> ' +
        this.self.abrName +
        "</div>" +
        "</div>" +
        '<div style="width: 33.33%">' +
        "</div>" +
        "</div>" +
        "</th>";
      tr2.innerHTML =
        "" +
        '<th colspan="5" style="padding: 6px 0px">' +
        '<div align="right">' +
        this.$t("content") +
        ": " +
        this.content +
        " </div>" +
        '<div align="right">' +
        this.$t("number") +
        ": " +
        this.number +
        " </div>" +
        '<div align="right">' +
        this.$t("type") +
        ": " +
        this.$t(this.type) +
        " </div>" +
        '<div align="right">' +
        this.$t("date") +
        ": " +
        this.date +
        " </div>" +
        "</th>" +
        '<th colspan="2" style="padding: 6px 0px">' +
        '<div align="right">' +
        this.$t("turningNumber") +
        ": " +
        this.turningNumber +
        " </div>" +
        '<div align="right">' +
        this.$t("dailyNumber") +
        ": " +
        this.dailyNumber +
        " </div>" +
        '<div align="right">' +
        this.$t("registerar") +
        ": " +
        this.user.name +
        " </div>" +
        '<div align="right">' +
        this.time +
        " </div>" +
        "</th>";
      elm.prepend(tr2);
      elm.prepend(tr1);
    },
    show() {
      var app = this;
      app.attachments = null;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "accounting/" + this.idProp + "/edit", {
          params: {
            id: this.idProp,
            print: "active",
          },
        })
        .then((res) => {
          app.$nextTick(() => {
            res = res.data;
            // attachment condition for  AccountingPrint//
            if (
              app.$options.name === "AccountingPrint" &&
              res.attachments != null &&
              Array.isArray(res.attachments) &&
              res.attachments.length > 0
            ) {
              app.attachments = res.attachments;
            } else {
              app.attachments = null;
            }
            app.id = res.id;
            app.currency = res.currency;
            app.currency_amount = res.currency_amount;
            app.number = res.number;
            app.turningNumber = res.turning_number;
            app.dailyNumber = res.daily_number;
            app.content = res.content;
            app.date = res.date;
            app.time = res.time;
            app.type = res.type;
            app.items = res.article;
            app.account = res.customer;
            app.entrepot = res.entrepot ? res.entrepot.entrepot : null;
            app.dueDate = res.due_date;
            app.payments = res.payments;
            app.checks = res.checks;
            app.status = res.last_status ? res.last_status.status : null;
            app.discount = res.discount;
            app.totalDiscount = res.discount;
            app.totaltax = res.tax;
            app.user = res.user;
            app.customer = res.customer;
            app.self = res.self;
            var printData = {
              title: this.title,
              user: this.$store.state.full_name,
            };
            this.setheader(printData);
            setTimeout(function () {
              app.render = true;
            }, 1000);
          });
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    clearCustom() {
      this.height = null;
      this.width = null;
    },
    setCustomeSize() {
      this.printSize = this.width + "cm " + this.height + "cm";
      this.customSizeDialog = false;
    },
    print() {
      var direction = "ltr";
      if (this.$vuetify.rtl) {
        direction = "rtl";
      }
      var style =
        "<style>@page { size: " +
        this.printSize +
        "}" +
        // '@media print {html, body {zoom: 75%}}' +
        "@bottom-left {content: normal;margin: 0;}}" +
        "</style>";
      setTimeout(function () {
        // Get HTML to print from element
        const prtHtml = document.getElementById("print").innerHTML;

        let stylesHtml = "";
        let node = null;
        for (node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
          stylesHtml += node.outerHTML;
        }

        const WinPrint = window.open(
          "",
          "",
          "left=0,top=0,width=1100,height=900,toolbar=0,scrollbars=0,status=0"
        );
        WinPrint.document.write(
          "<!DOCTYPE html> <html> <head> " +
            stylesHtml +
            " " +
            style +
            ' </head> <body style="direction: ' +
            direction +
            '">' +
            prtHtml +
            " </body> </html>"
        );
        WinPrint.document.close();
        WinPrint.focus();
        setTimeout(function () {
          WinPrint.print();
          WinPrint.close();
        }, 2000);
      }, 50);
    },
  },
};
</script>

<style scoped>
.signature-append-row td {
  padding-bottom: 0 !important;
}
.attachment-print-td {
  padding-top: 10px !important;
  border-top: none !important;
}
.attachment-print-section {
  width: 100%;
}
.attachment-table-scroll {
  overflow: visible;
  max-height: none;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}
.attachment-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.attachment-table thead th {
  font-size: 12px !important;
  line-height: 1.25;
  font-weight: 600;
  background: #eeeeee;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 8px;
  text-align: right;
  white-space: nowrap;
}
.attachment-table tbody td {
  border-bottom: 1px solid #eee;
  padding: 4px 8px;
  vertical-align: middle;
}
.preview-cell {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.file-name-cell {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media print {
  html,
  body {
    width: 143mm;
    height: 70mm;
  }
}
</style>
